/**
 * 📝 IMPLEMENTATION GUIDE: APPROVAL POLICIES
 * ─────────────────────────────────────────────────────────────────────────────
 * PURPOSE: 
 * This tab acts as the "Global Master Switch" for every module (Salary Advance, Overtime, etc.).
 * It determines which approval logic engine the API will use during submission.
 * 
 * STRATEGIES:
 * 1. "Standard": Uses the individual employee's hierarchy (defined in 'Financial Approvers' tab).
 * 2. "Amount-Based": Uses global amount ranges (defined in 'Amount Thresholds' tab).
 * 
 * SETUP REQUIRED (DATABASE SEEDING):
 * This table will be EMPTY until you add the modules to your database. 
 * Run the following SQL to see data in this tab:
 * 
 * INSERT INTO ApprovalModulePolicies (ModuleName, StrategyType, IsActive, CreatedAt)
 * VALUES ('SalaryAdvance', 'Standard', 1, GETDATE());
 * 
 * WORKFLOW:
 * - Once seeded, the module will appear here.
 * - Use the "Switch" button to toggle logic.
 * - If set to "Amount-Based", go to the next tab to configure who approves what amount.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useEffect, useState } from 'react';
import { useApprovalPolicy } from '../../shared/useApprovalPolicy';
import { CreateTableNew as CreateTable } from "../../../../../common/APICommon";
import { StrategyTypes } from '../../../../../utils/enums';

function ApprovalPolicyTab() {
  const { policies, fetchPolicies, updatePolicy, loading } = useApprovalPolicy();

  // ADDED: Missing pagination state
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const handleToggle = async (policy) => {
    const newStrategy = policy.strategyType === StrategyTypes.STANDARD
      ? StrategyTypes.AMOUNT_BASED
      : StrategyTypes.STANDARD;
    await updatePolicy(policy.id, newStrategy);
  };

  const headers = ["Module Name", "Current Strategy", "Status", "Action"];
  const accessors = ["moduleName", "strategyType", "isActive", "actions"];

  const renderCustomCell = (row, accessor) => {
    if (accessor === "strategyType") {
      return (
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${row.strategyType === StrategyTypes.AMOUNT_BASED ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"
          }`}>
          {row.strategyType.toUpperCase()}
        </span>
      );
    }
    if (accessor === "actions") {
      return (
        <button
          onClick={() => handleToggle(row)}
          className="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors cursor-pointer border-none"
        >
          Switch to {row.strategyType === StrategyTypes.STANDARD ? "Amount-Based" : "Standard"}
        </button>
      );
    }
    return null;
  };

  return (
    <div className="p-2">
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> "Standard" uses the individual employee's hierarchy. "Amount-Based" uses global thresholds defined in the next tab.
        </p>
      </div>
      <CreateTable
        tableName="ApprovalPolicies"
        headers={headers}
        accessors={accessors}
        data={policies}
        customCellRenderer={renderCustomCell}
        // MODIFIED: Added missing pagination props
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={10}
        totalItems={policies?.length}
      />
    </div>
  );
}
export default ApprovalPolicyTab;